import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { useAppContext } from '../../contexts/AppContext';
import { Sectie } from './bouwstenen';
import { sprekerKleur, uniekeSprekers } from '../../utils/sprekers';
import { weergavenaam } from '../../utils/gebruikers';

// Versiebeheer stationslocaties — fase 3. Vervangt de Locatie I/II/III-schakelaar
// door een tijdlijn van plaatsingen (nieuwste bovenaan). Per versie het tijdstip
// en, uitklapbaar, de gekoppelde opmerkingen/transcripttekst. Uitgelicht via de
// gedeelde actieveVersieId (kaart ↔ tijdlijn).

// Akkoordstatus per locatie cyclet met één knop.
const AKKOORD_CYCLE = ['', 'akkoord', 'afgekeurd', 'vervallen'];
const AKKOORD_CONFIG = {
  '': { label: 'Markeer voor akkoord', variant: 'default', color: 'teal' },
  akkoord: { label: '✓ Akkoord', variant: 'filled', color: 'teal', badge: 'akkoord' },
  afgekeurd: { label: '✗ Afgekeurd', variant: 'filled', color: 'red', badge: 'afgekeurd' },
  vervallen: { label: '⦸ Vervallen', variant: 'filled', color: 'gray', badge: 'vervallen' },
};

export default function VersieTijdlijn({ projectnummer, selectedProject, setSelectedProject }) {
  const {
    fetchWithAuth, addToast, sessie,
    actieveVersieId, setActieveVersieId, versieRefresh, bumpVersieRefresh,
    ververBuurtProjecten,
  } = useAppContext();
  const [versies, setVersies] = useState([]);
  const [toonVluchtig, setToonVluchtig] = useState(false);
  // Voor welk station is de standaardselectie al gezet? Voorkomt dat een
  // herlaad (na slepen/kiezen) de handmatige selectie overschrijft.
  const gedefaulteerdRef = useRef(null);

  const laad = useCallback(async () => {
    if (!projectnummer) return;
    try {
      const res = await fetchWithAuth(`/api/projecten/${encodeURIComponent(projectnummer)}/versies`);
      const data = await res.json();
      if (data.success) {
        setVersies(data.versies);
        // Bij het openen van een (ander) station: selecteer standaard de gekozen
        // locatie, anders de nieuwste (zelfde regel als de kaartstip).
        if (gedefaulteerdRef.current !== projectnummer) {
          gedefaulteerdRef.current = projectnummer;
          const zichtbaar = data.versies.filter(v => !v.vluchtig);
          const gekozen = zichtbaar.find(v => v.gekozen);
          const nieuwste = zichtbaar.length
            ? zichtbaar.reduce((a, b) => (new Date(a.aangemaakt_op) >= new Date(b.aangemaakt_op) ? a : b))
            : null;
          const doelId = gekozen ? gekozen.id : (nieuwste ? nieuwste.id : null);
          if (doelId != null) setActieveVersieId(doelId);
        }
      }
    } catch (e) { /* tijdlijn blijft leeg */ }
  }, [projectnummer, fetchWithAuth, setActieveVersieId]);

  useEffect(() => { laad(); }, [laad, versieRefresh]);

  const nieuwsteId = versies.length
    ? versies.reduce((a, b) => (new Date(a.aangemaakt_op) >= new Date(b.aangemaakt_op) ? a : b)).id
    : null;

  // Uitlichten (en de kaart erheen laten vliegen) gebeurt via de gedeelde
  // actieveVersieId; het kaart-effect in App.js reageert daarop. Loopt er een
  // belangentafel, dan stuurt de selectie ook de transcriptkoppeling: vanaf nu
  // hangt het gesprek aan déze versie (tot een andere keuze of een verplaatsing).
  const selecteer = (v) => {
    const nieuw = actieveVersieId === v.id ? null : v.id;
    setActieveVersieId(nieuw);
    if (nieuw != null && sessie?.actieveSessie && v.id != null) {
      sessie.logEvent('versie-selectie', projectnummer, null, v.id);
    }
  };

  const kies = async (v, e) => {
    if (e) e.stopPropagation();
    const nieuw = !v.gekozen;
    try {
      const res = await fetchWithAuth(
        `/api/projecten/${encodeURIComponent(projectnummer)}/versies/${v.id}/kies`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ gekozen: nieuw }) },
      );
      const data = await res.json();
      if (!data.success) { addToast(data.error || 'Kiezen mislukt', 'error'); return; }
      laad();
      // De stip staat op de gekozen locatie: direct meeverplaatsen.
      ververBuurtProjecten && ververBuurtProjecten();
      if (setSelectedProject && selectedProject) {
        setSelectedProject({
          ...selectedProject,
          gekozen_lokatie: nieuw && v.versie_nr <= 3 ? v.versie_nr : null,
        });
      }
    } catch (err) {
      addToast('Kiezen mislukt', 'error');
    }
  };

  // Cyclus: geen → akkoord → afgekeurd → vervallen → geen
  const markeerAkkoord = async (v, e) => {
    if (e) e.stopPropagation();
    const idx = AKKOORD_CYCLE.indexOf(v.akkoord_status || '');
    const nieuw = AKKOORD_CYCLE[(idx + 1) % AKKOORD_CYCLE.length];
    try {
      const res = await fetchWithAuth(
        `/api/projecten/${encodeURIComponent(projectnummer)}/versies/${v.id}/akkoord`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: nieuw }) },
      );
      const data = await res.json();
      if (!data.success) { addToast(data.error || 'Status wijzigen mislukt', 'error'); return; }
      laad();
      // De stip op buurtniveau kleurt naar deze status; direct verversen zodat
      // je de wijziging niet pas na een rondje langs een ander niveau ziet.
      ververBuurtProjecten && ververBuurtProjecten();
    } catch (err) {
      addToast('Status wijzigen mislukt', 'error');
    }
  };

  const verwijder = async (v, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Locatie ${v.versie_nr} definitief verwijderen?`)) return;
    try {
      const res = await fetchWithAuth(
        `/api/projecten/${encodeURIComponent(projectnummer)}/versies/${v.id}`,
        { method: 'DELETE' },
      );
      const data = await res.json();
      if (!data.success) { addToast(data.error || 'Verwijderen mislukt', 'error'); return; }
      if (actieveVersieId === v.id) setActieveVersieId(null);
      laad();
      if (bumpVersieRefresh) bumpVersieRefresh();  // kaart hertekenen
    } catch (err) {
      addToast('Verwijderen mislukt', 'error');
    }
  };

  // Situatietekening (A3-PDF, 1:500) openen in een nieuw tabblad.
  // Het tabblad gaat synchroon bij de klik open (anders blokkeert de
  // popup-blocker het na de trage fetch) en krijgt de PDF zodra die er is.
  const [tekeningBezig, setTekeningBezig] = useState(null);
  const situatietekening = async (v, e) => {
    if (e) e.stopPropagation();
    const venster = window.open('', '_blank');
    if (venster) {
      venster.document.write(
        '<title>Situatietekening</title>'
        + '<body style="font-family:sans-serif;display:grid;place-items:center;height:90vh">'
        + '<p>Situatietekening wordt gemaakt… dit duurt enkele seconden.</p></body>',
      );
    }
    setTekeningBezig(v.id);
    try {
      const res = await fetchWithAuth(`/api/versies/${v.id}/situatietekening`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (venster) venster.close();
        addToast(data.error || 'Tekening genereren mislukt', 'error');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (venster) {
        venster.location = url;
      } else {
        // Popup toch geblokkeerd: bied de PDF als download aan
        const a = document.createElement('a');
        a.href = url;
        a.download = `situatietekening_${projectnummer}_locatie${v.versie_nr}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (err) {
      if (venster) venster.close();
      addToast('Tekening genereren mislukt', 'error');
    } finally {
      setTekeningBezig(null);
    }
  };

  // Gekozen versie bovenaan, daarna nieuwste eerst
  const gesorteerd = [...versies].sort(
    (a, b) =>
      (Number(b.gekozen) - Number(a.gekozen)) ||
      (new Date(b.aangemaakt_op) - new Date(a.aangemaakt_op)) ||
      (b.versie_nr - a.versie_nr),
  );
  const zichtbaar = toonVluchtig ? gesorteerd : gesorteerd.filter(v => !v.vluchtig);
  const vluchtigAantal = versies.filter(v => v.vluchtig).length;

  return (
    <Sectie titel={`Locatie-tijdlijn (${versies.length})`}>
      {versies.length === 0 && (
        <Text size="xs" c="dimmed">
          Nog geen plaatsing vastgelegd. Sleep het station op de kaart om een
          eerste locatie te maken.
        </Text>
      )}
      {zichtbaar.map(v => {
        const open = actieveVersieId === v.id;
        return (
          <div key={v.id}
            style={{
              borderLeft: `3px solid ${v.gekozen ? 'var(--mantine-color-groen-6)' : (open ? 'var(--mantine-color-blue-4)' : 'var(--mantine-color-gray-3)')}`,
              paddingLeft: 8, paddingBottom: 6, marginBottom: 4,
            }}>
            <UnstyledButton onClick={() => selecteer(v)} style={{ width: '100%' }}>
              <Group gap={6} wrap="nowrap" justify="space-between">
                <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                  <Text size="sm" fw={600}>Locatie {v.versie_nr}</Text>
                  {v.id === nieuwsteId && <Badge size="xs" color="blue" variant="light">nieuwste</Badge>}
                  {v.gekozen && <Badge size="xs" color="groen">gekozen</Badge>}
                  {v.akkoord_status && (
                    <Badge size="xs" color={AKKOORD_CONFIG[v.akkoord_status].color}>
                      {v.akkoord_status}
                    </Badge>
                  )}
                  {v.vluchtig && <Badge size="xs" color="gray" variant="light">tussenstap</Badge>}
                </Group>
                <Text size="xs" c="dimmed">{open ? '▲' : '▼'}</Text>
              </Group>
              <Text size="xs" c="dimmed" ta="left">
                {v.aangemaakt_op ? new Date(v.aangemaakt_op).toLocaleString('nl-NL') : ''}
                {v.aangemaakt_door ? ` · ${weergavenaam(v, v.aangemaakt_door)}` : ''}
              </Text>
            </UnstyledButton>
            {open && (
              <Stack gap={6} mt={4}>
                {v.belangentafels && v.belangentafels.length > 0 ? (
                  v.belangentafels.map((bt, bi) => {
                    const sprekers = uniekeSprekers(bt.segmenten);
                    return (
                      <div key={bi}
                        style={{ borderLeft: '2px solid var(--mantine-color-gray-2)', paddingLeft: 8 }}>
                        <Text size="xs" fw={600}>
                          {bt.belangentafel_nr != null ? `Belangentafel ${bt.belangentafel_nr}` : 'Opmerkingen'}
                          {bt.gestart_op ? ` · ${new Date(bt.gestart_op).toLocaleDateString('nl-NL')}` : ''}
                        </Text>
                        {bt.deelnemers && bt.deelnemers.length > 0 && (
                          <Text size="xs" c="dimmed">Aanwezig: {bt.deelnemers.join(', ')}</Text>
                        )}
                        <Text size="xs" style={{ lineHeight: 1.5 }}>
                          {bt.segmenten.map((seg, si) => (
                            <Text key={si} span style={{ color: sprekerKleur(seg.spreker, sprekers) }}>
                              {seg.tekst}{' '}
                            </Text>
                          ))}
                        </Text>
                      </div>
                    );
                  })
                ) : (
                  <Text size="xs" c="dimmed">Nog geen gekoppelde tekst voor deze locatie.</Text>
                )}
                <Group gap={4} mt={2} justify="space-between">
                  <Group gap={4}>
                    <Button size="compact-xs" color="groen"
                      variant={v.gekozen ? 'filled' : 'default'} onClick={(e) => kies(v, e)}>
                      {v.gekozen ? '★ Gekozen' : 'Markeer als gekozen'}
                    </Button>
                    <Button size="compact-xs"
                      color={AKKOORD_CONFIG[v.akkoord_status || ''].color}
                      variant={AKKOORD_CONFIG[v.akkoord_status || ''].variant}
                      onClick={(e) => markeerAkkoord(v, e)}>
                      {AKKOORD_CONFIG[v.akkoord_status || ''].label}
                    </Button>
                    <Button size="compact-xs" variant="default"
                      loading={tekeningBezig === v.id} onClick={(e) => situatietekening(v, e)}>
                      Situatietekening
                    </Button>
                  </Group>
                  <Button size="compact-xs" color="red" variant="subtle" onClick={(e) => verwijder(v, e)}>
                    Verwijder
                  </Button>
                </Group>
              </Stack>
            )}
          </div>
        );
      })}
      {vluchtigAantal > 0 && (
        <Button size="compact-xs" variant="subtle" color="gray"
          onClick={() => setToonVluchtig(v => !v)}>
          {toonVluchtig ? 'Verberg tussenstappen' : `Toon ${vluchtigAantal} tussenstap${vluchtigAantal > 1 ? 'pen' : ''}`}
        </Button>
      )}
    </Sectie>
  );
}
